import React, { useState } from 'react';
import { X, Shield, AlertCircle, CheckCircle } from 'lucide-react';
import { useAuth } from '../../context/AuthContext';
import { reportListing } from '../../services/api';

const REPORT_REASONS = [
  { id: 'spam', label: 'Spam or misleading', description: 'Fake listing, spam, or misleading information' },
  { id: 'prohibited', label: 'Prohibited item', description: 'Item violates marketplace policies' },
  { id: 'fraud', label: 'Suspected fraud', description: 'Potential scam or fraudulent activity' },
  { id: 'harassment', label: 'Harassment', description: 'Offensive content or harassment' },
  { id: 'wrong_category', label: 'Wrong category', description: 'Item listed in incorrect category' },
  { id: 'duplicate', label: 'Duplicate listing', description: 'Same item listed multiple times' },
  { id: 'sold', label: 'Already sold', description: 'Item is no longer available' },
  { id: 'pricing', label: 'Incorrect pricing', description: 'Price seems inaccurate or misleading' },
  { id: 'other', label: 'Other reason', description: 'Another issue not listed above' },
];

const ReportModal = ({ isOpen, listing, onClose }) => {
  const [selectedReason, setSelectedReason] = useState('');
  const [customReason, setCustomReason] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const { user } = useAuth();

  const handleSubmit = async () => {
    if (!selectedReason) {
      alert('Please select a reason for reporting');
      return;
    }

    if (selectedReason === 'other' && !customReason.trim()) {
      alert('Please provide details for your report');
      return;
    }

    if (!user) {
      alert('Please log in to report a listing');
      return;
    }

    setIsSubmitting(true);

    try {
      const reportData = {
        listing_id: listing.listing_id,
        reason: selectedReason,
        description: selectedReason === 'other' ? customReason : REPORT_REASONS.find(r => r.id === selectedReason)?.label,
        reported_by: user.user_id
      };

      await reportListing(reportData);
      setIsSubmitted(true);
      
      // Auto close after 3 seconds
      setTimeout(() => {
        handleClose();
      }, 3000);
    } catch (error) {
      // console.error('Error submitting report:', error);
      alert('Failed to submit report. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleClose = () => {
    setSelectedReason('');
    setCustomReason('');
    setIsSubmitted(false);
    onClose();
  };

  const getSelectedReasonDescription = () => {
    return REPORT_REASONS.find(reason => reason.id === selectedReason)?.description || '';
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      <div className="flex items-center justify-center min-h-screen px-4 pt-4 pb-20 text-center sm:block sm:p-0">
        {/* Overlay */}
        <div 
          className="fixed inset-0 transition-opacity bg-gray-500 bg-opacity-75" 
          onClick={handleClose}
        ></div>

        {/* Modal */}
        <div className="inline-block align-bottom bg-white rounded-2xl text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-lg sm:w-full">
          {/* Header */}
          <div className="bg-white px-6 py-4 border-b flex items-center justify-between">
            <div>
              <h3 className="text-lg font-bold text-gray-900">Report Listing</h3>
              <p className="text-sm text-gray-600">
                Help us keep the marketplace safe and trustworthy
              </p>
            </div>
            <button
              onClick={handleClose}
              className="text-gray-400 hover:text-gray-500"
            >
              <X className="w-6 h-6" />
            </button>
          </div>

          {isSubmitted ? (
            /* Success State */
            <div className="px-6 py-8 text-center">
              <div className="mx-auto flex items-center justify-center h-16 w-16 rounded-full bg-green-100 mb-4">
                <CheckCircle className="h-10 w-10 text-green-600" />
              </div>
              <h3 className="text-lg font-medium text-gray-900 mb-2">Report Submitted</h3>
              <p className="text-gray-600 mb-6">
                Thank you for your report. We will review this listing and take appropriate action.
              </p>
              <div className="bg-green-50 rounded-lg p-4">
                <p className="text-sm text-green-800">
                  Your report is anonymous. The seller will not be notified about who reported their listing.
                </p>
              </div>
            </div>
          ) : (
            /* Report Form */
            <>
              <div className="px-6 py-4 max-h-[60vh] overflow-y-auto">
                {/* Listing Preview */}
                <div className="bg-gray-50 rounded-lg p-4 mb-6">
                  <h4 className="font-medium text-gray-900 mb-1">{listing?.title}</h4>
                  <p className="text-sm text-gray-600">
                    {listing?.category_name} {listing?.subcategory_name && `› ${listing.subcategory_name}`}
                  </p>
                </div>

                {/* Report Reasons */}
                <div className="mb-6">
                  <h4 className="font-medium text-gray-900 mb-3">Why are you reporting this listing?</h4>
                  <p className="text-sm text-gray-600 mb-4">
                    Select the most appropriate reason. Your report will be reviewed by our moderation team.
                  </p>

                  <div className="space-y-3">
                    {REPORT_REASONS.map((reason) => (
                      <label
                        key={reason.id}
                        className={`flex items-start p-4 rounded-lg border-2 cursor-pointer transition-all ${
                          selectedReason === reason.id
                            ? 'border-orange-500 bg-orange-50'
                            : 'border-gray-200 hover:border-gray-300'
                        }`}
                      >
                        <div className="flex items-center h-5">
                          <input
                            type="radio"
                            name="report-reason"
                            checked={selectedReason === reason.id}
                            onChange={() => setSelectedReason(reason.id)}
                            className="h-4 w-4 text-orange-600 border-gray-300 focus:ring-orange-500"
                          />
                        </div>
                        <div className="ml-3 flex-1">
                          <span className="font-medium text-gray-900">{reason.label}</span>
                          <p className="text-sm text-gray-600">{reason.description}</p>
                        </div>
                      </label>
                    ))}
                  </div>
                </div>

                {/* Additional Details */}
                {selectedReason === 'other' && (
                  <div className="mb-6">
                    <label className="block text-sm font-medium text-gray-900 mb-3">
                      Please provide details
                    </label>
                    <textarea
                      value={customReason}
                      onChange={(e) => setCustomReason(e.target.value)}
                      rows={4}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent"
                      placeholder="Describe the issue in detail..."
                    />
                  </div>
                )}

                {/* Selected Reason Info */}
                {selectedReason && selectedReason !== 'other' && (
                  <div className="bg-orange-50 rounded-lg p-4 mb-6">
                    <div className="flex">
                      <AlertCircle className="h-5 w-5 text-orange-600 flex-shrink-0" />
                      <p className="ml-3 text-sm text-orange-800">
                        {getSelectedReasonDescription()}
                      </p>
                    </div>
                  </div>
                )}

                {/* Safety Notice */}
                <div className="bg-green-50 rounded-lg p-4">
                  <div className="flex">
                    <Shield className="h-5 w-5 text-green-600 flex-shrink-0" />
                    <div className="ml-3">
                      <p className="text-sm font-medium text-green-800">Your Report is Anonymous</p>
                      <p className="text-sm text-green-700">
                        The seller will not be notified about who reported their listing. 
                        We take all reports seriously to maintain a safe community.
                      </p>
                    </div>
                  </div>
                </div>
              </div>

              {/* Footer Actions */}
              <div className="bg-gray-50 px-6 py-4 flex gap-3">
                <button
                  onClick={handleClose}
                  disabled={isSubmitting}
                  className="flex-1 px-4 py-3 border border-gray-300 rounded-lg text-gray-700 font-medium hover:bg-gray-50 disabled:opacity-50"
                >
                  Cancel
                </button>
                <button
                  onClick={handleSubmit}
                  disabled={isSubmitting || !selectedReason || (selectedReason === 'other' && !customReason.trim())}
                  className="flex-1 px-4 py-3 bg-orange-500 text-white rounded-lg font-medium hover:bg-orange-600 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {isSubmitting ? 'Submitting...' : 'Submit Report'}
                </button>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default ReportModal;